import { useState } from "react";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import NoDataImage from "assets/images/no-data2.svg";
import ConsultDebriefContent from "components/consult-debrief";
import { useGenerateDebriefMutation } from "services/ambient-scribe";
import {
  useConfirmConsultOutcomeMutation,
  useGetConsultDebriefQuery
} from "services/consult-debriefs";

// Fallback for a network/parsing failure where no message body is available at all —
// the backend always sends a real `error` string for an actual 400 (ENG-1724).
const GENERIC_GENERATE_ERROR_MESSAGE =
  "Something went wrong. Please try again.";

// The backend owns the message text (`err.data.error`) — this only maps its `reason`
// code to a tone. An unrecognized/missing reason defaults to "error", the safest
// severity for a message we can't otherwise classify.
const REASON_SEVERITY: Record<string, "info" | "warning" | "error"> = {
  not_completed: "info",
  transcript_not_ready: "info",
  feature_disabled: "warning",
  practice_disabled: "warning"
};

type GenerateError = {
  message: string;
  severity: "info" | "warning" | "error";
};

type DebriefTabProps = {
  scribeId: number;
  consultDebriefId: number | null | undefined;
  /** Union of the local latch and whatever else drives the parent's poll (ENG-1706). */
  isGeneratingDebrief: boolean;
  /** True once the parent's generation latch has given up waiting (180s, see parent). */
  hasTimedOut: boolean;
  onGenerateStarted: () => void;
};

/**
 * The Scribe modal's "Debrief" tab (ENG-1706). Two jobs depending on whether a debrief
 * exists yet for this scribe:
 *
 * - `consultDebriefId` present → fetch and render it, same as `ConsultDebriefDialog`
 *   (Appointments report), via the shared `ConsultDebriefContent`.
 * - Absent → one of three trigger states (idle CTA / generating / timed-out). All
 *   generation-latch state (the "is it in flight" flag and the 180s timeout) lives in the
 *   PARENT (`view-scribe-details.component.tsx`), mirroring the existing
 *   `localGenerating`/`handleAudiologySummaryGenerateStarted` pattern used for the sibling
 *   audiology-summary flow — this component stays a pure state machine over props plus the
 *   debrief query/mutation it owns directly.
 */
export default function DebriefTab({
  scribeId,
  consultDebriefId,
  isGeneratingDebrief,
  hasTimedOut,
  onGenerateStarted
}: Readonly<DebriefTabProps>) {
  const {
    data: debrief,
    isLoading,
    isError
  } = useGetConsultDebriefQuery(consultDebriefId as number, {
    skip: !consultDebriefId
  });
  const [confirmOutcome, { isLoading: isConfirming, isError: confirmFailed }] =
    useConfirmConsultOutcomeMutation();

  const [generateDebrief, { isLoading: isRequestInFlight }] =
    useGenerateDebriefMutation();
  const [generateError, setGenerateError] = useState<GenerateError | null>(
    null
  );
  // Disables the button on the same tick as the click, ahead of RTK Query's own `isLoading`
  // flip — a slow network round-trip must not leave a window for a double-click.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isBusy = isSubmitting || isRequestInFlight;

  const handleGenerate = async () => {
    setGenerateError(null);
    setIsSubmitting(true);
    try {
      const result = await generateDebrief({ id: scribeId });

      // Success is a bare 202 with an empty body — there is no `{enqueued: true}` to read.
      // `result.data` is `undefined` here; asserting into it (as the old code did) is what
      // silently killed onGenerateStarted() on every real success.
      if ("error" in result) {
        const err = result.error as any;
        const reason = err?.data?.reason as string | undefined;
        setGenerateError({
          message: err?.data?.error ?? GENERIC_GENERATE_ERROR_MESSAGE,
          severity: (reason && REASON_SEVERITY[reason]) || "error"
        });
        return;
      }
      onGenerateStarted();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (consultDebriefId) {
    return (
      <ConsultDebriefContent
        debrief={debrief}
        isLoading={isLoading}
        isError={isError}
        isConfirming={isConfirming}
        confirmFailed={confirmFailed}
        onConfirmOutcome={(outcome) =>
          debrief && confirmOutcome({ id: debrief.id, outcome })
        }
      />
    );
  }

  if (isGeneratingDebrief) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        py={6}
        mt={2}
      >
        <CircularProgress size={32} />
        <Typography variant="body3" color="text.secondary">
          Generating debrief…
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This may take a moment. It will appear here automatically.
        </Typography>
      </Stack>
    );
  }

  if (hasTimedOut) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        py={6}
        mt={2}
      >
        <HourglassEmptyOutlinedIcon
          sx={{ fontSize: 32, color: "text.secondary" }}
        />
        <Typography variant="body3" color="text.secondary">
          This is taking longer than expected.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The debrief may still be generating in the background — try again in a
          moment.
        </Typography>
        <LoadingButton
          variant="contained"
          startIcon={<AutoFixHighIcon />}
          onClick={handleGenerate}
          loading={isBusy}
          disabled={isBusy}
        >
          Try Again
        </LoadingButton>
      </Stack>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      columnGap={2.5}
      alignItems="center"
      justifyContent="center"
      width="100%"
      minHeight="280px"
      mt={2}
    >
      <Box
        component="img"
        src={NoDataImage}
        alt=""
        sx={{ height: "130px", p: 2 }}
      />
      <Stack rowGap={3} alignItems={{ xs: "center", sm: "flex-start" }}>
        <Typography variant="body3" mb={1}>
          No debrief generated for this consult yet. Generate one from this
          recording.
        </Typography>
        <LoadingButton
          variant="contained"
          startIcon={<AutoFixHighIcon />}
          onClick={handleGenerate}
          loading={isBusy}
          disabled={isBusy}
        >
          Generate Debrief
        </LoadingButton>
        {generateError && (
          <Alert severity={generateError.severity} sx={{ width: "100%" }}>
            {generateError.message}
          </Alert>
        )}
      </Stack>
    </Stack>
  );
}
